import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, styled, TableBody, Tooltip, Typography } from '@mui/material';

import { Icon, RouterLink, Table, TableColumn, TableProps } from '@components/base';
import LayoutEntityFilterChips from '@components/content/LayoutEntityListPage/LayoutEntityFilterChips';
import TagFilter from '@components/content/LayoutEntityListPage/TagFilter';
import TypeFilter from '@components/content/LayoutEntityListPage/TypeFilter';
import useLayoutEntityFilterContext from '@hooks/useLayoutEntityFilter';
import useSearchContext from '@hooks/useSearchContext';
import { ContentType } from '@models/Content';
import LayoutEntity from '@models/LayoutEntity';
import TagColumn from '@modules/layouts/components/LayoutEntityTable/TagColumn';
import { numberComparator, stringComparator } from '@utils/arrayUtils';
import { formatDate, formatTimeAgo } from '@utils/dateUtils';
import { useLayoutEntityName } from '@utils/layoutEntityUtils';
import { reverseName } from '@utils/stringUtils';

import useSkeletonCount from './useSkeletonCount';

export type LayoutEntityTableProps<T extends LayoutEntity> = TableProps<T> & {
  contentType: ContentType;
  disableFilter?: boolean;
  disableNonEssentialCols?: boolean;
  disableSortable?: boolean;
  entityRoute?: (item: T) => string;
  hasTags?: boolean;
  openEntityInNewTab?: boolean;
};

type LayoutEntityWithTags<T extends LayoutEntity> = T & { tags: string[] };

export const StyledTableBody = styled(TableBody)`
  tr:last-of-type td {
    border-bottom: 0;
  }
`;

function LayoutEntityTable<T extends LayoutEntity>({
  columns,
  contentType,
  disableFilter,
  disableNonEssentialCols,
  disableSortable,
  emptyStateContext,
  entityRoute,
  hasTags,
  items,
  loading,
  openEntityInNewTab,
  order = 'desc',
  orderBy = 'updatedDateTime',
  selectable,
  TableContainerProps: tableContainerProps,
  tableSearch,
  ...rest
}: LayoutEntityTableProps<T>) {
  const { lcSearchLanguage } = useSearchContext();
  const { t } = useTranslation();
  const getEntityName = useLayoutEntityName();

  const {
    hasFilters,
    loading: filtersLoading,
    queryText,
    setQueryText,
    favoriteTags,
    toggleFavoriteTag,
  } = useLayoutEntityFilterContext<T>();

  const allColumns: TableColumn<T>[] = [
    ...(columns?.filter(({ isTypeColumn }) => isTypeColumn) ?? []),
    {
      comparator: (a, b) => getEntityName(a).localeCompare(getEntityName(b)),
      disableSortable,
      key: 'name',
      label: `${t('name')} (${lcSearchLanguage.toUpperCase()})`,
      TableCellProps: columns && columns.length > 2 ? { narrow: true } : {},
      value: item =>
        entityRoute ? (
          <RouterLink
            noWrap
            onClick={e => e.stopPropagation()}
            to={entityRoute(item)}
            underline='hover'
            variant='inherit'
            {...(openEntityInNewTab ? { target: '_blank' } : {})}
          >
            {getEntityName(item)}
            {openEntityInNewTab && (
              <Icon fontSize='inherit' name='openInNew' sx={{ verticalAlign: 'middle', ml: 0.5 }} />
            )}
          </RouterLink>
        ) : (
          getEntityName(item)
        ),
    },
    ...(disableNonEssentialCols
      ? []
      : [
          {
            label: t('slug'),
            disableSortable,
            key: 'slug',
            value: item => (
              <Typography fontSize={13} noWrap title={item.slug}>
                <code>{item.slug}</code>
              </Typography>
            ),
          } as TableColumn<T>,
        ]),
    ...(columns?.filter(({ isTypeColumn }) => !isTypeColumn) ?? []),
    ...(hasTags && !disableNonEssentialCols
      ? [
          {
            comparator: (
              { tags: aTags }: LayoutEntityWithTags<T>,
              { tags: bTags }: LayoutEntityWithTags<T>
            ) => numberComparator(aTags.length, bTags.length),
            key: 'tags',
            disableSortable,
            label: t('tags'),
            numeric: true,
            TableCellProps: { sx: {} },
            value: (item: LayoutEntityWithTags<T>) => <TagColumn entityTags={item.tags} />,
          } as TableColumn<T>,
        ]
      : []),
    {
      key: 'updatedDateTime',
      label: t('updatedAt'),
      disableSortable,
      value: item => (
        <Tooltip placement='left' title={formatDate(item.updatedDateTime)}>
          <span>{formatTimeAgo(item.updatedDateTime)}</span>
        </Tooltip>
      ),
    },
    ...(disableNonEssentialCols
      ? []
      : [
          {
            comparator: (a, b) =>
              stringComparator(reverseName(a.updatedBy), reverseName(b.updatedBy)),
            key: 'updatedBy',
            disableSortable,
            label: t('updatedBy'),
            TableCellProps: { narrow: true },
            value: item => reverseName(item.updatedBy),
          } as TableColumn<T>,
        ]),
  ];

  const skeletonCount = useSkeletonCount(contentType, items);

  return (
    <Table
      {...rest}
      columns={allColumns}
      emptyStateContext={emptyStateContext}
      filterActions={
        !disableFilter && (
          <Fragment>
            <TagFilter favoriteTags={favoriteTags} toggleFavorite={toggleFavoriteTag} />
            <TypeFilter />
          </Fragment>
        )
      }
      filterChips={
        !disableFilter &&
        !filtersLoading &&
        hasFilters && (
          <Stack spacing={0.5}>
            <LayoutEntityFilterChips />
          </Stack>
        )
      }
      items={items}
      loading={loading}
      order={order}
      orderBy={orderBy}
      selectable={selectable}
      skeletonCount={skeletonCount}
      TableContainerProps={tableContainerProps}
      tableSearch={(tableSearch ?? tableSearch === false) ? undefined : { queryText, setQueryText }}
    />
  );
}

export default LayoutEntityTable;
